// Lyria RealTime WebSocket client for browser
import * as Sentry from "@sentry/react";

export type LyriaMessageType =
  | 'connected'
  | 'disconnected'
  | 'audio'
  | 'playing'
  | 'paused'
  | 'stopped'
  | 'error'
  | 'filtered'
  | 'warning';

export interface LyriaMessage {
  type: LyriaMessageType;
  data?: string; // base64 audio for 'audio' type
  message?: string;
}

export interface WeightedPrompt {
  text: string;
  weight: number;
}

export interface MusicConfig {
  bpm?: number;
  density?: number;
  brightness?: number;
  temperature?: number;
  guidance?: number;
  scale?: string;
  muteBass?: boolean;
  muteDrums?: boolean;
  seed?: number;
  topK?: number;
  onlyBassAndDrums?: boolean;
  musicGenerationMode?: 'QUALITY' | 'DIVERSITY' | 'VOCALIZATION';
}

const VALID_SCALE_VALUES = new Set([
  "C_MAJOR_A_MINOR",
  "D_FLAT_MAJOR_B_FLAT_MINOR",
  "D_MAJOR_B_MINOR",
  "E_FLAT_MAJOR_C_MINOR",
  "E_MAJOR_D_FLAT_MINOR",
  "F_MAJOR_D_MINOR",
  "G_FLAT_MAJOR_E_FLAT_MINOR",
  "G_MAJOR_E_MINOR",
  "A_FLAT_MAJOR_F_MINOR",
  "A_MAJOR_G_FLAT_MINOR",
  "B_FLAT_MAJOR_G_MINOR",
  "B_MAJOR_A_FLAT_MINOR",
  "SCALE_UNSPECIFIED",
]);

const sanitizeWeight = (weight: number): number => {
  if (!Number.isFinite(weight)) return 1;
  const clamped = Math.max(-2, Math.min(2, weight));
  if (Math.abs(clamped) < 0.01) return clamped < 0 ? -0.01 : 0.01;
  return Number(clamped.toFixed(2));
};

const sanitizePrompts = (prompts: WeightedPrompt[]): WeightedPrompt[] =>
  prompts
    .map((prompt) => ({
      text: prompt.text.trim(),
      weight: sanitizeWeight(prompt.weight),
    }))
    .filter((prompt) => prompt.text.length > 0);

export type LyriaState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'starting'
  | 'playing'
  | 'paused'
  | 'buffering';

export class LyriaClient {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private crossfadeGainNode: GainNode | null = null; // For crossfade during reconnect
  private analyserNode: AnalyserNode | null = null;
  private nextPlayTime = 0;
  private activeSources = new Set<AudioBufferSourceNode>();
  private state: LyriaState = 'disconnected';
  private volume = 1.0;

  // Playback intent tracking: UI should only show "playing" once we have real audio.
  private playIntent = false;
  private hasReceivedAudioSincePlay = false;
  
  // Audio buffer for seamless playback during reconnection
  private audioBuffer: Array<{ data: Float32Array; scheduledTime: number }> = [];
  private maxBufferChunks = 50; // ~5 seconds of audio at typical chunk rates
  private lastAudioTime = 0;
  
  // Connection health monitoring
  private lastMessageTime = 0;
  private healthCheckInterval: number | null = null;
  // Proactive reconnect was originally very aggressive and can cause unwanted restarts.
  // Prefer stability: only reconnect after sustained audio silence.
  private staleThresholdMs = 15000;

  // Error reporting throttling
  private lastAudioErrorTime = 0;
  private audioErrorThrottleMs = 5000; // Only show audio errors once per 5 seconds

  // Reconnection state
  private reconnectAttempts = 0;
  private reconnectDelay = 300;
  private lastPrompts: WeightedPrompt[] = [];
  private lastConfig: MusicConfig = {};
  private wasPlaying = false;
  private shouldAutoReconnect = true;
  private isReconnecting = false;
  
  // Crossfade settings for seamless reconnection
  private crossfadeDuration = 1.5; // 1.5 second crossfade
  private isCrossfading = false;
  private crossfadeStartTime = 0;

  // Metrics for observability (Sentry context)
  private sessionStartMs = Date.now();
  private audioChunksProcessed = 0;
  private lastBreadcrumbAudioMs = 0;
  private lastUnderrunBreadcrumbMs = 0;
  private promptsSetCount = 0;
  private configSetCount = 0;

  private onStateChange: (state: LyriaState) => void;
  private onError: (message: string) => void;
  private onAudioLevel: (level: number) => void;
  private onPromptFiltered: ((prompt: string) => void) | null = null;

  constructor(
    onStateChange: (state: LyriaState) => void,
    onError: (message: string) => void,
    onAudioLevel: (level: number) => void,
    onPromptFiltered?: (prompt: string) => void
  ) {
    this.onStateChange = onStateChange;
    this.onError = onError;
    this.onAudioLevel = onAudioLevel;
    this.onPromptFiltered = onPromptFiltered || null;
  }

  private updateState(state: LyriaState) {
    const prev = this.state;
    this.state = state;
    this.onStateChange(state);

    if (prev !== state) {
      Sentry.addBreadcrumb({
        category: "lyria.state",
        level: "info",
        message: "state change",
        data: { from: prev, to: state },
      });
    }
  }

  private ensureAudioGraph() {
    if (this.audioContext && this.audioContext.state !== 'closed') return;

    this.audioContext = new AudioContext({ sampleRate: 48000 });

    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this.volume;
    this.gainNode.connect(this.audioContext.destination);

    this.crossfadeGainNode = this.audioContext.createGain();
    this.crossfadeGainNode.gain.value = 1.0;

    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 1024;
    this.analyserNode.smoothingTimeConstant = 0.92;

    this.crossfadeGainNode.connect(this.analyserNode);
    this.analyserNode.connect(this.gainNode);
  }

  async primeAudio(): Promise<void> {
    this.ensureAudioGraph();
    if (!this.audioContext) return;

    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // A very short silent buffer unlocks output devices in stricter Web Audio implementations.
      const buffer = this.audioContext.createBuffer(1, 1, this.audioContext.sampleRate);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.gainNode ?? this.audioContext.destination);
      source.start();
    } catch (error) {
      Sentry.addBreadcrumb({
        category: "audio",
        level: "warning",
        message: "audio prime failed",
        data: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[LyriaClient] Already connected');
      return;
    }

    this.updateState('connecting');
    this.shouldAutoReconnect = true;
    
    this.ensureAudioGraph();
    this.nextPlayTime = 0;
    this.audioBuffer = [];
    this.lastMessageTime = Date.now();
    this.isCrossfading = false;

    const workerUrl = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';
    const wsUrl = workerUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const endpoint = `${wsUrl}/lyria-realtime`;

    console.log('[LyriaClient] Connecting to:', endpoint);
    Sentry.addBreadcrumb({
      category: "lyria.websocket",
      level: "info",
      message: "connect",
      data: { host: new URL(endpoint).host },
    });

    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(endpoint);

      this.ws.onopen = () => {
        console.log('[LyriaClient] WebSocket connected');
        Sentry.addBreadcrumb({ category: "lyria.websocket", level: "info", message: "open" });
        // Give health-check a grace period before judging the stream as stale.
        this.lastAudioTime = Date.now();

        this.reconnectAttempts = 0;
        this.startHealthCheck();
      };

      this.ws.onmessage = (event) => {
        this.lastMessageTime = Date.now();

        try {
          const message: LyriaMessage = JSON.parse(event.data);

          this.handleMessage(message);

          if (message.type === 'connected') {
            this.updateState('connected');
            resolve();
          }
        } catch (error) {
          console.error('[LyriaClient] Failed to parse message:', error);
          Sentry.captureException(error);
          // Only attempt reconnect if we haven't established a connection yet
          // During active playback, a single bad message shouldn't trigger reconnection
          if (this.state === 'connecting') {
            this.onError('Received invalid data from server. Reconnecting...');
            setTimeout(() => {
              this.connect().catch((err) =>
                console.error('[LyriaClient] Reconnect failed:', err)
              );
            }, 2000);
          }
        }
      };

      this.ws.onerror = (error) => {
        console.error('[LyriaClient] WebSocket error:', error);
        Sentry.addBreadcrumb({ category: "lyria.websocket", level: "error", message: "error" });
        Sentry.withScope((scope) => {
          scope.setTag("sf_component", "lyria-client");
          scope.setExtra("wsReadyState", this.ws?.readyState);
          Sentry.captureException(new Error("WebSocket connection error"));
        });
        if (!this.isReconnecting) {
          this.onError('Connection error');
        }
        reject(new Error("WebSocket connection error"));
      };

      this.ws.onclose = (event) => {
        console.log('[LyriaClient] WebSocket closed, code:', event.code);
        Sentry.addBreadcrumb({
          category: "lyria.websocket",
          level: "info",
          message: "close",
          data: { code: event.code },
        });
        this.stopHealthCheck();

        const wasPlayingBefore =
          this.state === 'playing' || this.state === 'starting' || this.state === 'buffering';

        // If auto-reconnect enabled, always try to reconnect
        if (this.shouldAutoReconnect) {
          this.wasPlaying = wasPlayingBefore;
          // Show buffering immediately to avoid "Not connected" flash
          if (wasPlayingBefore) {
            this.updateState('buffering');
          }
          this.attemptReconnect();
        } else if (!this.isReconnecting) {
          this.updateState('disconnected');
        }
      };

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.state === 'connecting') {
          reject(new Error('Connection timeout'));
          this.disconnect();
        }
      }, 30000);
    });
  }

  private startHealthCheck() {
    this.stopHealthCheck();
    
    // Delay first health check to allow session restoration to complete
    const initialDelay = this.reconnectAttempts > 0 ? 5000 : 1000;
    
    setTimeout(() => {
      this.healthCheckInterval = window.setInterval(() => {
        const now = Date.now();
        const timeSinceLastAudio = this.lastAudioTime ? now - this.lastAudioTime : Infinity;
        
        // If playing and no audio for too long, proactively reconnect
        // Use longer threshold after reconnection to allow session to stabilize
        const effectiveThreshold = this.reconnectAttempts > 0 ? 25000 : this.staleThresholdMs;
        
        if ((this.state === 'playing' || this.state === 'starting' || this.state === 'buffering') && 
            timeSinceLastAudio > effectiveThreshold &&
            !this.isReconnecting &&
            !this.isCrossfading) {
          console.log('[LyriaClient] Connection appears stale, proactive reconnect...');
          this.wasPlaying = true;
          this.proactiveReconnect();
        }
      }, 2000);
    }, initialDelay);
  }

  private stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  private async proactiveReconnect() {
    if (this.isReconnecting) return;

    // Don't update state to disconnected - keep playing from buffer
    this.isReconnecting = true;

    // Close current connection
    if (this.ws) {
      this.ws.onclose = null; // Prevent normal close handler
      this.ws.close();
      this.ws = null;
    }

    await this.attemptReconnect();
  }

  private async attemptReconnect() {
    if (this.isReconnecting && this.reconnectAttempts > 0) {
      return; // Already reconnecting
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    console.log(`[LyriaClient] Reconnect attempt ${this.reconnectAttempts}...`);

    // Show buffering state if we were playing
    if (this.wasPlaying && this.state !== 'buffering') {
      this.updateState('buffering');
    }

    // Exponential backoff with max 30 seconds
    const baseDelay = Math.min(this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1), 30000);
    const jitter = Math.random() * 1000;
    await new Promise(resolve => setTimeout(resolve, baseDelay + jitter));

    try {
      // Don't call full connect() - do inline reconnect to preserve audio context
      const workerUrl = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';
      const wsUrl = workerUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      const endpoint = `${wsUrl}/lyria-realtime`;

      await new Promise<void>((resolve, reject) => {
        this.ws = new WebSocket(endpoint);

        const timeout = setTimeout(() => {
          reject(new Error('Reconnect timeout'));
        }, 10000);

        this.ws.onopen = () => {
          console.log('[LyriaClient] Reconnected');
          // Reset audio clock on reconnect to avoid immediate stale reconnect loops.
          this.lastAudioTime = Date.now();

          this.lastMessageTime = Date.now();
          this.startHealthCheck();
        };

        this.ws.onmessage = (event) => {
          this.lastMessageTime = Date.now();

          try {
            const message: LyriaMessage = JSON.parse(event.data);

            if (message.type === 'connected') {
              clearTimeout(timeout);
              this.isReconnecting = false;
              // Reset lastMessageTime to give more grace period after reconnect
              this.lastMessageTime = Date.now();
              resolve();

              // Restore state after a brief stabilization period
              setTimeout(() => {
                this.restoreSession();
                // Only reset reconnectAttempts after successful audio resumes
                setTimeout(() => {
                  this.reconnectAttempts = 0;
                }, 3000);
              }, 100);
            }

            this.handleMessage(message);
          } catch (error) {
            console.error('[LyriaClient] Failed to parse message during reconnection:', error);
            // Don't reject the promise for parse errors - the connection may still be viable
            // Only log and continue, allowing subsequent messages to be processed
            // If the connection is truly broken, onclose or onerror will handle it
          }
        };

        this.ws.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Reconnect failed'));
        };

        this.ws.onclose = () => {
          clearTimeout(timeout);
          // Continue retrying if auto-reconnect enabled
          if (this.shouldAutoReconnect) {
            this.attemptReconnect();
          } else {
            this.isReconnecting = false;
            this.updateState('disconnected');
            this.onError('Connection lost. Please refresh.');
          }
        };
      });

    } catch (error) {
      console.error('[LyriaClient] Reconnect failed:', error);

      // Continue retrying indefinitely if auto-reconnect is enabled
      if (this.shouldAutoReconnect) {
        this.attemptReconnect(); // Retry forever
      } else {
        this.isReconnecting = false;
        this.onError('Connection lost. Please refresh.');
        this.updateState('disconnected');
      }
    }
  }

  private restoreSession() {
    console.log('[LyriaClient] Restoring session...');
    // Reconnects are a new upstream stream; crossfade to hide clicks/gaps.
    this.startCrossfade();

    // Small delay to ensure connection is stable
    setTimeout(() => {
      if (this.lastPrompts.length > 0) {
        console.log('[LyriaClient] Restoring prompts (reconnect)');
        this.send({ type: 'setPrompts', prompts: this.lastPrompts });
      }

      if (Object.keys(this.lastConfig).length > 0) {
        console.log('[LyriaClient] Restoring config (reconnect)');
        this.send({ type: 'setConfig', ...this.lastConfig });
      }

      if (this.wasPlaying) {
        console.log('[LyriaClient] Resuming playback');
        setTimeout(() => {
          this.send({ type: 'play' });
          // Don't show "playing" until we actually receive audio again.
          this.playIntent = true;
          this.hasReceivedAudioSincePlay = false;
          this.wasPlaying = false;
        }, 100);
      }
    }, 50);
  }

  private startCrossfade() {
    if (!this.audioContext || !this.crossfadeGainNode) return;

    console.log('[LyriaClient] Starting audio crossfade');
    this.isCrossfading = true;
    this.crossfadeStartTime = this.audioContext.currentTime;

    // Start with new audio quiet and fade in
    this.crossfadeGainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
    this.crossfadeGainNode.gain.linearRampToValueAtTime(
      1.0,
      this.audioContext.currentTime + this.crossfadeDuration
    );

    // Mark crossfade complete after duration
    setTimeout(() => {
      this.isCrossfading = false;
      console.log('[LyriaClient] Crossfade complete');
    }, this.crossfadeDuration * 1000);
  }

  private handleMessage(message: LyriaMessage) {
    switch (message.type) {
      case 'audio':
        if (message.data) {
          this.audioChunksProcessed += 1;
          const now = Date.now();
          // Throttle audio breadcrumbs heavily to avoid noise.
          if (now - this.lastBreadcrumbAudioMs > 5000) {
            this.lastBreadcrumbAudioMs = now;
            Sentry.addBreadcrumb({
              category: "audio",
              level: "info",
              message: "audio chunks processed",
              data: {
                audioChunksProcessed: this.audioChunksProcessed,
                bufferChunks: this.audioBuffer.length,
              },
            });
          }
          this.processAudio(message.data);
        }
        break;
      case 'playing':
        // If audio already arrived, we can confidently show "playing".
        if (this.playIntent && this.hasReceivedAudioSincePlay) {
          this.updateState('playing');
        } else if (this.playIntent && this.state !== 'buffering') {
          // Otherwise, show an explicit "starting" state until the first audio chunk arrives.
          this.updateState('starting');
        }
        break;
      case 'paused':
        this.playIntent = false;
        this.stopScheduledAudio();
        this.updateState('paused');
        break;
      case 'stopped':
        this.playIntent = false;
        this.hasReceivedAudioSincePlay = false;
        this.updateState('connected');
        this.stopScheduledAudio();
        break;
      case 'error':
        console.error('[LyriaClient] Server error:', message.message);
        if (message.message?.includes('disconnected') || message.message?.includes('closed')) {
          if (this.shouldAutoReconnect && !this.isReconnecting) {
            this.wasPlaying =
              this.state === 'playing' || this.state === 'starting' || this.state === 'buffering';
            this.proactiveReconnect();
            return;
          }
        }
        this.onError(message.message || 'Unknown error');
        break;
      case 'filtered':
        console.log('[LyriaClient] Prompt filtered:', message.message);
        Sentry.addBreadcrumb({
          category: "prompt.filter",
          level: "warning",
          message: "prompt filtered",
          data: { promptsCount: this.lastPrompts.length },
        });
        // Notify callback with the last prompt that was filtered
        if (this.onPromptFiltered && this.lastPrompts.length > 0) {
          const lastPromptText = this.lastPrompts.map(p => p.text).join(', ');
          this.onPromptFiltered(lastPromptText);
        }
        break;
      case 'warning':
        if (message.message) {
          console.log('[LyriaClient] Warning:', message.message);
          Sentry.addBreadcrumb({
            category: "lyria.warning",
            level: "warning",
            message: message.message,
          });
        }
        break;
    }
  }

  private processAudio(base64Data: string) {
    if (!this.audioContext) return;
    if (!this.playIntent) {
      this.lastAudioTime = Date.now();
      return;
    }

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    try {
      // Decode base64 to binary
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert 16-bit PCM to Float32 (stereo interleaved)
      const int16Array = new Int16Array(bytes.buffer);
      const floatData = new Float32Array(int16Array.length);
      
      for (let i = 0; i < int16Array.length; i++) {
        floatData[i] = int16Array[i] / 32768.0;
      }

      // Calculate audio level for visualization
      let sum = 0;
      for (let i = 0; i < floatData.length; i++) {
        sum += Math.abs(floatData[i]);
      }
      const avgLevel = sum / floatData.length;
      this.onAudioLevel(avgLevel);

      // Flip into "playing" only once we've received real audio after a play request.
      if (this.playIntent && !this.hasReceivedAudioSincePlay) {
        this.hasReceivedAudioSincePlay = true;
        if (this.state === 'starting' || this.state === 'buffering' || this.state === 'connected') {
          this.updateState('playing');
        }
      }

      // Play audio immediately (low-latency approach)
      this.playAudio(floatData);

    } catch (error) {
      console.error('[LyriaClient] Audio processing error:', error);
      Sentry.withScope((scope) => {
        scope.setTag("sf_component", "frontend");
        scope.setTag("sf_feature", "audio");
        Sentry.captureException(error);
      });

      // Throttle error notifications to prevent toast flooding
      // Only show one audio error per 5 seconds
      const now = Date.now();
        if (now - this.lastAudioErrorTime > this.audioErrorThrottleMs) {
          this.lastAudioErrorTime = now;
          this.onError(
            error instanceof Error
              ? `Audio playback error: ${error.message}. Try pausing and playing again.`
              : 'Failed to process audio chunk. If this persists, refresh the page.'
          );
        }
    }
  }

  private playAudio(floatData: Float32Array) {
    if (!this.audioContext || !this.gainNode || !this.crossfadeGainNode) return;

    const numSamples = floatData.length / 2; // stereo
    const audioBuffer = this.audioContext.createBuffer(2, numSamples, 48000);
    
    const leftChannel = audioBuffer.getChannelData(0);
    const rightChannel = audioBuffer.getChannelData(1);

    for (let i = 0; i < numSamples; i++) {
      leftChannel[i] = floatData[i * 2];
      rightChannel[i] = floatData[i * 2 + 1];
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.onended = () => {
      this.activeSources.delete(source);
    };
    
    // Route through crossfade gain node for smooth reconnection
    source.connect(this.crossfadeGainNode);

    const currentTime = this.audioContext.currentTime;
    
    // Keep chunks contiguous when possible. If we underrun, recover quickly instead of
    // inserting a noticeable fixed silence before every late chunk.
    const firstChunkLead = 0.12;
    const underrunLead = 0.025;
    const minSafeLead = 0.015;
    let startTime: number;

    if (this.nextPlayTime === 0) {
      startTime = currentTime + firstChunkLead;
    } else if (this.nextPlayTime < currentTime + minSafeLead) {
      startTime = currentTime + underrunLead;
      const now = Date.now();
      if (now - this.lastUnderrunBreadcrumbMs > 5000) {
        this.lastUnderrunBreadcrumbMs = now;
        Sentry.addBreadcrumb({
          category: "audio",
          level: "warning",
          message: "audio scheduler underrun",
          data: {
            driftMs: Math.round((currentTime - this.nextPlayTime) * 1000),
          },
        });
      }
    } else {
      startTime = this.nextPlayTime;
    }
    
    source.start(startTime);
    this.activeSources.add(source);
    this.nextPlayTime = startTime + audioBuffer.duration;
    this.lastAudioTime = Date.now();
    
    // Store in buffer for potential reconnection overlap
    this.audioBuffer.push({ data: floatData, scheduledTime: startTime });
    
    // Trim buffer to max size
    while (this.audioBuffer.length > this.maxBufferChunks) {
      this.audioBuffer.shift();
    }
  }

  private stopScheduledAudio() {
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        // Source may have already ended. The onended handler will remove it.
      }
    }
    this.activeSources.clear();
    this.audioBuffer = [];
    this.nextPlayTime = 0;
  }

  private send(data: object) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[LyriaClient] Cannot send - WebSocket not open');
    }
  }

  setPrompts(prompts: WeightedPrompt[]) {
    const sanitizedPrompts = sanitizePrompts(prompts);
    this.promptsSetCount += 1;
    this.lastPrompts = sanitizedPrompts;
    Sentry.addBreadcrumb({
      category: "prompts",
      level: "info",
      message: "setPrompts (client)",
      data: { count: sanitizedPrompts.length },
    });
    if (sanitizedPrompts.length === 0) {
      this.onError('No playable prompts are active.');
      return;
    }
    this.send({ type: 'setPrompts', prompts: sanitizedPrompts });
  }

  setConfig(config: MusicConfig) {
    this.configSetCount += 1;
    const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
    const sanitized: MusicConfig = { ...config };

    if (typeof sanitized.bpm === "number") sanitized.bpm = clamp(sanitized.bpm, 60, 200);
    if (typeof sanitized.density === "number") sanitized.density = clamp(sanitized.density, 0, 1);
    if (typeof sanitized.brightness === "number") sanitized.brightness = clamp(sanitized.brightness, 0, 1);
    if (typeof sanitized.temperature === "number") sanitized.temperature = clamp(sanitized.temperature, 0, 3);
    if (typeof sanitized.guidance === "number") sanitized.guidance = clamp(sanitized.guidance, 0, 6);
    if (typeof sanitized.topK === "number") sanitized.topK = clamp(sanitized.topK, 1, 1000);
    if (typeof sanitized.seed === "number") {
      // Keep within signed 32-bit int range (positive).
      sanitized.seed = Math.floor(clamp(sanitized.seed, 1, 0x7fffffff));
    }
    if (typeof sanitized.scale === "string") {
      if (!VALID_SCALE_VALUES.has(sanitized.scale)) delete sanitized.scale;
    }

    this.lastConfig = { ...this.lastConfig, ...sanitized };
    Sentry.addBreadcrumb({
      category: "config",
      level: "info",
      message: "setConfig (client)",
      data: { keys: Object.keys(sanitized) },
    });
    this.send({ type: 'setConfig', ...sanitized });
  }

  play() {
    this.playIntent = true;
    this.hasReceivedAudioSincePlay = false;
    if (this.state === 'connected' || this.state === 'paused') {
      this.updateState('starting');
    }
    this.send({ type: 'play' });
  }

  pause() {
    this.playIntent = false;
    this.stopScheduledAudio();
    // Optimistic UI update; server will also confirm with a 'paused' message.
    if (this.state !== 'disconnected' && this.state !== 'connecting') {
      this.updateState('paused');
    }
    this.send({ type: 'pause' });
  }

  stop() {
    this.wasPlaying = false;
    this.playIntent = false;
    this.hasReceivedAudioSincePlay = false;
    this.stopScheduledAudio();
    this.send({ type: 'stop' });
  }

  resetContext() {
    this.stopScheduledAudio();
    this.hasReceivedAudioSincePlay = false;
    this.send({ type: 'resetContext' });
  }

  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.gainNode && this.audioContext) {
      this.gainNode.gain.setValueAtTime(this.volume, this.audioContext.currentTime);
    }
  }

  getVolume(): number {
    return this.volume;
  }

  disconnect() {
    this.shouldAutoReconnect = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.wasPlaying = false;

    this.stopHealthCheck();
    this.stopScheduledAudio();

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.analyserNode = null;
    this.updateState('disconnected');
  }

  getState(): LyriaState {
    return this.state;
  }

  getAnalyserNode(): AnalyserNode | null {
    return this.analyserNode;
  }

  getSentryMetrics() {
    return {
      sessionDurationSeconds: Math.floor((Date.now() - this.sessionStartMs) / 1000),
      reconnectAttempts: this.reconnectAttempts,
      audioChunksProcessed: this.audioChunksProcessed,
      audioBufferChunks: this.audioBuffer.length,
      promptsSetCount: this.promptsSetCount,
      configSetCount: this.configSetCount,
    };
  }
}
